import { useEffect } from 'react'

/**
 * Marker stashed on the synthetic history entry this hook pushes, so the
 * cleanup path can tell "we are still sitting on our own pushed entry"
 * (UI-driven close → pop it back off) from "the user already pressed Back"
 * (popstate already consumed it → nothing to pop).
 */
const HISTORY_MARKER = 'mmRouteDetailsModal'

interface MarkedHistoryState {
  readonly [HISTORY_MARKER]?: true
}

/**
 * Make the browser Back button DISMISS a transient in-app overlay instead
 * of navigating the document away (Bug 5).
 *
 * The RouteDetails phone modal is pure React state (`detailsOpen`) with
 * NO URL entry of its own, so before this hook the Back button popped the
 * last *real* history entry — landing on a prerendered SEO shell that never
 * re-hydrates ("bare page"). StationDetails never had the bug because it
 * is URL-driven (`/stations/:slug`), so Back is an ordinary same-document
 * react-router pop. This hook gives the overlay the same property WITHOUT a
 * URL change: while it is open we push one synthetic same-URL history entry;
 * Back pops that entry (a same-document popstate → the SPA stays mounted)
 * and we translate it into `onDismiss()`.
 *
 * Coexistence with react-router's `BrowserRouter`: we `pushState` the SAME
 * URL and SPREAD the existing `history.state` (react-router's `{ usr, key,
 * idx }`) so router bookkeeping is preserved — the Back pop resolves to the
 * unchanged location and triggers no navigation.
 *
 * @param isOpen whether the overlay is currently mounted / visible.
 * @param onDismiss close the overlay (idempotent — may be called after it
 *   is already closed during cleanup).
 */
export function useBrowserBackDismiss(isOpen: boolean, onDismiss: () => void): void {
  useEffect(() => {
    if (!isOpen) return

    const current = (window.history.state ?? null) as MarkedHistoryState | null
    // Preserve react-router's own state fields; add our marker on top.
    window.history.pushState({ ...current, [HISTORY_MARKER]: true }, '')

    const onPopState = (): void => {
      onDismiss()
    }
    window.addEventListener('popstate', onPopState)

    return (): void => {
      window.removeEventListener('popstate', onPopState)
      // If we are unmounting for any reason OTHER than the user pressing
      // Back (backdrop tap / Escape / close button / route-status change),
      // our synthetic entry is still the current one — pop it so the history
      // stack stays balanced and a later Back doesn't strand the user on a
      // dead forward entry. When Back caused this cleanup, the popstate has
      // already moved off our entry, so the marker is gone and we skip.
      const state = (window.history.state ?? null) as MarkedHistoryState | null
      if (state?.[HISTORY_MARKER] === true) {
        window.history.back()
      }
    }
  }, [isOpen, onDismiss])
}
